import { includes } from '@hover/utilities';
import type { TFunction } from 'i18next';
import type {
  AiImageInstanceEdit,
  AiImageRegionEdits,
  AiImageWallEdits,
} from 'mock/generated/types';
import { useMemo } from 'react';

import { isAiGenerated, isDisplay } from 'src/modules/homeownerApp/types';
import { useProductCatalogVariationsQuery } from 'src/shared/graphql/generated';
import type {
  AiDisplayImageFragment,
  AiGeneratedImageFragment,
  AiImageEdits,
  AiImageMaterialEditFragment,
  AiImagePaintEditFragment,
  ProductCatalogVariationFragment,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import type { Region } from '../constants/customizationCategories';
import { warn } from '../helpers/logging';

export type RegionWithDisabledRegions = Region | 'fence';

// This is effectively camelcased RegionWithDisabledRegions
type AiImageEditsKeysWithoutMetadata = Omit<
  AiImageEdits,
  'schemaVersion' | '__typename'
>;

export interface VariationEdit {
  key: string;
  kind: 'paint' | 'material';
  variationUuid: string;
  rawRegion: RegionWithDisabledRegions;
  region: string;
  assetsMap: Map<number, AiDisplayImageFragment>;
  variation?: ProductCatalogVariationFragment | null;
}

const SurfaceClassToRegion: Record<
  string,
  RegionWithDisabledRegions | undefined
> = {
  Door: 'door',
  Wall: 'walls',
  Roof: 'roof',
  Window: 'windows',
  Shutter: 'shutter',
  GarageDoor: 'garage_door',
  Soffit: 'trim',
  Fascia: 'trim',
  WindowTrim: 'trim',
  DoorTrim: 'trim',
  OtherTrim: 'trim',
};

export const mapRegionToDisplayText = (
  t: TFunction,
  region: RegionWithDisabledRegions,
) => t(`regions.${region}`);

export const buildEditKey = (region: string, variationUuid: string) =>
  `${region}-${variationUuid}`;

export const getSnakecaseRegionFromKey = (
  camelCaseRegion: keyof AiImageEdits,
): RegionWithDisabledRegions | undefined => {
  if (includes(['__typename', 'schemaVersion', 'instances'], camelCaseRegion)) {
    return undefined;
  }
  let region: RegionWithDisabledRegions;
  switch (camelCaseRegion) {
    case 'garageDoor':
      region = 'garage_door';
      break;
    default:
      region = camelCaseRegion as RegionWithDisabledRegions;
      break;
  }

  return region;
};

const addVariationEdit = (
  variationEditMap: Map<string, VariationEdit>,
  edit:
    | AiImagePaintEditFragment
    | AiImageMaterialEditFragment
    | null
    | undefined,
  region: RegionWithDisabledRegions,
  regionDisplayName: string,
  kind: 'paint' | 'material',
  image: AiGeneratedImageFragment | AiDisplayImageFragment,
) => {
  if (!edit?.productCatalogVariationUuid) {
    return;
  }

  const variationUuid = edit.productCatalogVariationUuid;
  const editKey = buildEditKey(region, variationUuid);
  if (variationEditMap.has(editKey)) {
    if (isDisplay(image)) {
      const variationEdit = variationEditMap.get(editKey);
      // Using an assets map with the image id as the key ensures instance edits
      // do not add duplicate images to the VariationEdit
      variationEdit?.assetsMap?.set(image.id, image);
    }
  } else {
    variationEditMap.set(editKey, {
      key: editKey,
      variationUuid,
      rawRegion: region,
      region: regionDisplayName,
      kind,
      assetsMap: isDisplay(image)
        ? new Map([[image.id, image]])
        : new Map<number, AiDisplayImageFragment>(),
    });
  }
};

const addVariationEdits = (
  t: TFunction,
  variationEditMap: Map<string, VariationEdit>,
  edit: AiImageRegionEdits | AiImageWallEdits | AiImageInstanceEdit,
  region: RegionWithDisabledRegions,
  image: AiGeneratedImageFragment | AiDisplayImageFragment,
) => {
  const regionDisplayName = mapRegionToDisplayText(t, region);

  addVariationEdit(
    variationEditMap,
    edit.paint,
    region,
    regionDisplayName,
    'paint',
    image,
  );
  addVariationEdit(
    variationEditMap,
    edit.material,
    region,
    regionDisplayName,
    'material',
    image,
  );
};

export const useVariationEdits = (
  images: (AiGeneratedImageFragment | AiDisplayImageFragment)[],
) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const variationEditsByEditKey = useMemo(() => {
    const variationEditsMap: Map<string, VariationEdit> = new Map();

    images.forEach(image => {
      const allEdits =
        isAiGenerated(image) && !!image.metadata?.allEdits
          ? image.metadata.allEdits
          : isDisplay(image) &&
            (image.aiGeneratedImage.metadata?.allEdits || {});

      Object.keys(allEdits).forEach(key => {
        const region = getSnakecaseRegionFromKey(key as keyof AiImageEdits);

        if (!region && key !== 'instances') return;

        const edit = (allEdits as AiImageEdits)[
          key as keyof AiImageEditsKeysWithoutMetadata
        ];

        if (!edit) return;

        if (Array.isArray(edit)) {
          edit.forEach(instanceEdit => {
            const instanceRegion =
              SurfaceClassToRegion[instanceEdit.surfaceClass];
            if (!instanceRegion) return;

            addVariationEdits(
              t,
              variationEditsMap,
              instanceEdit,
              instanceRegion,
              image,
            );
          });
        } else if (region) {
          addVariationEdits(t, variationEditsMap, edit, region, image);
        } else {
          warn(`No region available for edit: ${JSON.stringify(edit)}`);
        }
      });
    });

    return variationEditsMap;
  }, [images, t]);

  const uniqueVariationUuids = useMemo(() => {
    const variationUuidsSet = new Set<string>();
    Array.from(variationEditsByEditKey.values()).forEach(edit =>
      variationUuidsSet.add(edit.variationUuid),
    );
    return Array.from(variationUuidsSet);
  }, [variationEditsByEditKey]);

  const { data, loading } = useProductCatalogVariationsQuery({
    variables: {
      variationIds: uniqueVariationUuids,
    },
    skip: uniqueVariationUuids.length === 0,
  });

  const enrichedVariationEditsByEditKey = useMemo(() => {
    const nodes = data?.productCatalogVariations.nodes;
    const productVariationsByUuid = nodes
      ? nodes.reduce((accum, node: ProductCatalogVariationFragment | null) => {
          if (node?.id) {
            accum.set(node.id, node);
          }
          return accum;
        }, new Map<string, ProductCatalogVariationFragment>())
      : new Map<string, ProductCatalogVariationFragment>();

    Array.from(variationEditsByEditKey.values()).forEach(edit => {
      const variation = productVariationsByUuid.get(edit.variationUuid);
      if (variation) {
        edit.variation = variation;
      }
    });

    return variationEditsByEditKey;
  }, [data, variationEditsByEditKey]);

  return useMemo(
    () => ({
      variationEditsByEditKey: enrichedVariationEditsByEditKey,
      loading,
    }),
    [loading, enrichedVariationEditsByEditKey],
  );
};
